/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { TaskStatusIcon } from "components/TaskStatusIcon/TaskStatusIcon";
import { QuickActionCopyRow } from "../layout/DataTableQuickActionBar/QuickActionCopyRow";
import { QuickActionRunRow } from "../layout/DataTableQuickActionBar/QuickActionRunRow";
import { QuickActionDeleteRow } from "../layout/DataTableQuickActionBar/QuickActionDeleteRow";
import { QuickActionCancelTask } from "../layout/DataTableQuickActionBar/QuickActionCancelTask";
import { isTerminalTaskStatus } from "utils/isTerminalTaskStatus";
import { QuickActionToggleRowJoinable } from "../layout/DataTableQuickActionBar/QuickActionToggleRowJoinable";
import { GridApi } from "ag-grid-community";
import { DataTableRowData } from "../layout/DataTable";

const styles = {
  root: css`
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
    padding: 0px 11px;
    background-color: inherit;
    gap: 2px;
  `,
};

interface CellRendererQuickActionsProps {
  rowId: string;
  gridApi: GridApi<DataTableRowData> | undefined;
  tableId: string;
  readOnly?: boolean;
}

const CellRendererQuickActions = ({
  rowId,
  gridApi,
  tableId,
  readOnly,
}: CellRendererQuickActionsProps) => {
  const row = useDataTableContext((s) => {
    const table = s.tables.find((table) => table.id === tableId);
    return table?.rows.find((row) => row.id === rowId);
  });
  const columns = useDataTableContext((s) => {
    const table = s.tables.find((table) => table.id === tableId);
    return table?.columns;
  });

  if (columns === undefined || row === undefined) {
    return null;
  }

  return (
    <span css={styles.root}>
      {row.task === null ? (
        <QuickActionRunRow
          readOnly={readOnly}
          tableId={tableId}
          rowId={row.id}
          gridApi={gridApi}
        />
      ) : (
        <TaskStatusIcon
          taskId={row.task.id}
          taskStatus={row.task.status}
          taskCreated={row.task.date_created}
        />
      )}

      {row.task !== null && !isTerminalTaskStatus(row.task.status) ? (
        <QuickActionCancelTask
          readOnly={readOnly}
          tableId={tableId}
          rowId={row.id}
        />
      ) : (
        <QuickActionDeleteRow
          readOnly={readOnly}
          tableId={tableId}
          rowId={row.id}
        />
      )}

      <QuickActionCopyRow
        readOnly={readOnly}
        tableId={tableId}
        rowId={row.id}
      />

      <QuickActionToggleRowJoinable
        readOnly={readOnly}
        tableId={tableId}
        rowId={row.id}
        isJoinable={row.joinable}
      />
    </span>
  );
};

export const CellRendererQuickActionsMemo = memo(CellRendererQuickActions);
